import { request } from '../../app/api';
import {
  createAnalyticsFilterQuery,
  type AnalyticsFilterScope,
} from './AnalyticsFilterScope';
import type {
  AnalyticsEventRecord,
  AppVersionOption,
  CountryOption,
  InstallationPage,
  InstallationSummary,
  SessionEventPage,
  SessionPage,
  SessionSummary,
} from './types';

export const ANALYTICS_PAGE_SIZE = 50;
const BULK_LIST_PAGE_SIZE = 200;
const BULK_EVENT_PAGE_SIZE = 500;

export async function loadAppVersions(
  environmentId: string,
  filters: AnalyticsFilterScope,
): Promise<AppVersionOption[]> {
  const query = createAnalyticsFilterQuery(environmentId, filters);
  query.delete('appVersion');
  const response = await request<{ appVersions: AppVersionOption[] }>(
    `/admin/v1/reports/app-versions?${query.toString()}`,
  );
  return response.appVersions;
}

export async function loadCountries(
  environmentId: string,
  filters: AnalyticsFilterScope,
): Promise<CountryOption[]> {
  const query = createAnalyticsFilterQuery(environmentId, filters);
  query.delete('country');
  const response = await request<{ countries: CountryOption[] }>(
    `/admin/v1/reports/countries?${query.toString()}`,
  );
  return response.countries;
}

export async function loadAllInstallations(environmentId: string): Promise<InstallationSummary[]> {
  const installations: InstallationSummary[] = [];
  let pageNumber = 1;
  let totalPages = 1;
  do {
    const page = await loadInstallationPage(environmentId, '', pageNumber, BULK_LIST_PAGE_SIZE);
    installations.push(...page.installations);
    pageNumber = page.pagination.page + 1;
    totalPages = page.pagination.totalPages;
  } while (pageNumber <= totalPages);
  return installations;
}

export async function loadInstallationPage(
  environmentId: string,
  platformCode: string,
  page: number,
  pageSize = ANALYTICS_PAGE_SIZE,
): Promise<InstallationPage> {
  const query = createPaginationQuery(environmentId, page, pageSize);
  if (platformCode !== '') query.set('platformCode', platformCode);
  return request<InstallationPage>(`/admin/v1/reports/installations?${query.toString()}`);
}

export async function loadScopedInstallationPage(
  environmentId: string,
  filters: AnalyticsFilterScope,
  page: number,
): Promise<InstallationPage> {
  const query = createAnalyticsFilterQuery(environmentId, filters);
  appendPagination(query, page, ANALYTICS_PAGE_SIZE);
  return request<InstallationPage>(`/admin/v1/reports/installations?${query.toString()}`);
}

export async function loadAllInstallationSessions(
  environmentId: string,
  installationId: string,
  platformCodes: readonly number[] = [],
): Promise<SessionSummary[]> {
  const sessions: SessionSummary[] = [];
  let pageNumber = 1;
  let totalPages = 1;
  do {
    const page = await loadInstallationSessionPage(
      environmentId,
      installationId,
      pageNumber,
      platformCodes,
      BULK_LIST_PAGE_SIZE,
    );
    sessions.push(...page.sessions);
    pageNumber = page.pagination.page + 1;
    totalPages = page.pagination.totalPages;
  } while (pageNumber <= totalPages);
  return sessions;
}

export async function loadInstallationSessionPage(
  environmentId: string,
  installationId: string,
  page: number,
  platformCodes: readonly number[] = [],
  pageSize = ANALYTICS_PAGE_SIZE,
): Promise<SessionPage> {
  const query = createPaginationQuery(environmentId, page, pageSize);
  if (platformCodes.length > 0) query.set('platformCodes', platformCodes.join(','));
  return request<SessionPage>(`/admin/v1/reports/installations/${encodeURIComponent(installationId)}/sessions?${query.toString()}`);
}

export async function loadScopedInstallationSessionPage(
  environmentId: string,
  installationId: string,
  filters: AnalyticsFilterScope,
  page: number,
): Promise<SessionPage> {
  const query = createAnalyticsFilterQuery(environmentId, filters);
  appendPagination(query, page, ANALYTICS_PAGE_SIZE);
  return request<SessionPage>(`/admin/v1/reports/installations/${encodeURIComponent(installationId)}/sessions?${query.toString()}`);
}

export async function loadCompleteSession(
  environmentId: string,
  sessionId: string,
): Promise<AnalyticsEventRecord[]> {
  const events: AnalyticsEventRecord[] = [];
  let pageNumber = 1;
  let totalPages = 1;
  do {
    const page = await loadSessionEventPage(environmentId, sessionId, pageNumber, BULK_EVENT_PAGE_SIZE);
    events.push(...page.events);
    pageNumber = page.pagination.page + 1;
    totalPages = page.pagination.totalPages;
  } while (pageNumber <= totalPages);
  events.sort(compareEventSequences);
  return events;
}

export async function loadSessionEventPage(
  environmentId: string,
  sessionId: string,
  page: number,
  pageSize = ANALYTICS_PAGE_SIZE,
): Promise<SessionEventPage> {
  const query = createPaginationQuery(environmentId, page, pageSize);
  return request<SessionEventPage>(`/admin/v1/reports/sessions/${encodeURIComponent(sessionId)}?${query.toString()}`);
}

function createPaginationQuery(environmentId: string, page: number, pageSize: number): URLSearchParams {
  const query = new URLSearchParams({ environmentId });
  appendPagination(query, page, pageSize);
  return query;
}

function appendPagination(query: URLSearchParams, page: number, pageSize: number): void {
  query.set('page', String(page));
  query.set('pageSize', String(pageSize));
}

function compareEventSequences(left: AnalyticsEventRecord, right: AnalyticsEventRecord): number {
  return left.sequence - right.sequence;
}
